// QuinnPage.jsx — Quinn Executive & Life Assistant Studio
// 4 tabs: Chat · Calendar · Research · Preferences
// Think Pepper Potts — calm, capable, always three steps ahead.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import './QuinnPage.css';
import { useProfile } from '../context/UserProfileContext';
import { apiFetch } from '../lib/api';

// ── Parse [ACTION:create_task ...] tag from Quinn's response ──────────────────
function parseTaskAction(text) {
  const match = text.match(/\[ACTION:create_task([^\]]*)\]/);
  if (!match) return null;
  const attrs = match[1];
  const get = (key) => {
    const m = attrs.match(new RegExp(key + '="([^"]*)"'));
    return m ? m[1].trim() : '';
  };
  return {
    name:        get('name'),
    taskType:    get('taskType'),
    dueDate:     get('dueDate'),
    accountName: get('accountName'),
    priority:    get('priority') || 'This Week',
  };
}

function stripTaskAction(text) {
  return text.replace(/\[ACTION:create_task[^\]]*\]\s*/g, '').trim();
}

// ── Parse [ACTION:add_calendar|EventName|DateISO|EndDateISO|EventType|ContactName] ──
function parseCalendarAction(text) {
  const match = text.match(/\[ACTION:add_calendar\|([^\]]*)\]/);
  if (!match) return null;
  const parts = match[1].split('|');
  return {
    eventName:   (parts[0] || '').trim(),
    date:        (parts[1] || '').trim(),
    endDate:     (parts[2] || '').trim() || null,
    eventType:   (parts[3] || 'Meeting').trim(),
    contactName: (parts[4] || '').trim(),
  };
}

function stripCalendarAction(text) {
  return text.replace(/\[ACTION:add_calendar[^\]]*\]\s*/g, '').trim();
}

// ── Markdown renderer ─────────────────────────────────────────────────────────
function MarkdownContent({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { elements.push(<br key={i} />); i++; continue; }
    if (line.startsWith('### ')) { elements.push(<h3 key={i}>{line.slice(4)}</h3>); i++; continue; }
    if (line.startsWith('## '))  { elements.push(<h2 key={i}>{line.slice(3)}</h2>); i++; continue; }
    if (line.startsWith('# '))   { elements.push(<h1 key={i}>{line.slice(2)}</h1>); i++; continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(<li key={i}>{lines[i].slice(2)}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`}>{items}</ul>);
      continue;
    }
    const formatted = line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>');
    elements.push(<p key={i} dangerouslySetInnerHTML={{ __html: formatted }} />);
    i++;
  }
  return <div className="quinn-markdown">{elements}</div>;
}

// ── Event type colors ─────────────────────────────────────────────────────────
const EVENT_COLORS = {
  'Meeting':             'var(--dm-blue-mid)',
  'Cadence Touchpoint':  '#e2c97e',
  'Email/LinkedIn':      'var(--dm-purple-mid)',
  'Task Due':            '#f59e0b',
  'Birthday':            '#ec4899',
  'Anniversary':         '#ec4899',
  'Kids Event':          '#22c55e',
  'Personal':            'var(--dm-grey-600)',
  'Vacation':            '#06b6d4',
  'Health':              '#ef4444',
  'Subscription Renewal':'#f97316',
  'Expense':             '#84cc16',
};

function eventColor(type) {
  return EVENT_COLORS[type] || 'var(--dm-grey-600)';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 0 && diff <= 6) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── CHAT TAB ─────────────────────────────────────────────────────────────────
const QUINN_PROMPTS = [
  "What do I need to focus on today?",
  "What's on my calendar this week?",
  "Find me flights to Chicago next week.",
  "Add milk and eggs to groceries.",
  "What medications need a refill soon?",
  "Research hotels in Nashville for March 28.",
];

function ChatTab({ onNavigate }) {
  const { timezone = 'America/New_York', firstName } = useProfile();
  const [thread, setThread]               = useState([]);
  const [input, setInput]                 = useState('');
  const [isThinking, setIsThinking]       = useState(false);
  const [error, setError]                 = useState(null);
  const [pendingTaskActions, setPending]      = useState({});   // msgIndex → taskAction
  const [taskConfirmed, setTaskConfirmed]     = useState({});   // msgIndex → boolean
  const [pendingCalActions, setCalPending]    = useState({});   // msgIndex → calAction
  const [calConfirmed, setCalConfirmed]       = useState({});   // msgIndex → status
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const threadRef = useRef([]);

  useEffect(() => { threadRef.current = thread; }, [thread]);
  useEffect(() => { return () => saveSession(threadRef.current); }, []); // eslint-disable-line

  const saveSession = async (t) => {
    const msgs = (t || []).filter(m => m.content);
    if (msgs.length < 2) return;
    try {
      // Jarvis session-close — cross-room visibility into Quinn Studio
      apiFetch('/api/jarvis/session-close', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thread: msgs.map(m => ({ role: m.role, ai: m.role === 'assistant' ? 'quinn' : undefined, content: m.content })),
          context: { accountId: null, accountName: null },
          sessionType: 'quinn',
          actionsLog: [], proposedQueueItems: [],
        }),
      }).catch(() => {});

      await apiFetch('/api/quinn/save-session', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread: msgs, accountId: null, accountName: null, context: {} }),
      });
    } catch (err) { console.warn('[QuinnPage] save error:', err.message); }
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread, isThinking]);
  useEffect(() => { if (!isThinking) inputRef.current?.focus(); }, [isThinking]);

  const confirmTask = async (msgIndex, taskAction) => {
    setTaskConfirmed(prev => ({ ...prev, [msgIndex]: 'loading' }));
    try {
      let accountId = null;
      if (taskAction.accountName) {
        const acctRes = await apiFetch('/api/accounts/list');
        const acctData = await acctRes.json();
        if (acctData.accounts) {
          const nameLower = taskAction.accountName.toLowerCase();
          const match = acctData.accounts.find(a =>
            (a.accountName || '').toLowerCase().includes(nameLower) ||
            nameLower.includes((a.accountName || '').toLowerCase())
          );
          if (match) accountId = match.id;
        }
      }
      const res = await apiFetch('/api/tasks/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: taskAction.name, taskType: taskAction.taskType,
          dueDate: taskAction.dueDate, priority: taskAction.priority,
          status: 'Suggested', accountId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTaskConfirmed(prev => ({ ...prev, [msgIndex]: 'done' }));
        setPending(prev => { const n = { ...prev }; delete n[msgIndex]; return n; });
      } else {
        setTaskConfirmed(prev => ({ ...prev, [msgIndex]: 'error' }));
      }
    } catch {
      setTaskConfirmed(prev => ({ ...prev, [msgIndex]: 'error' }));
    }
  };

  const confirmCalendar = async (msgIndex, calAction) => {
    setCalConfirmed(prev => ({ ...prev, [msgIndex]: 'loading' }));
    try {
      const res = await apiFetch('/api/floor/action', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'add_calendar',
          data:   calAction,
          context: {},
        }),
      });
      const data = await res.json();
      if (data.success) {
        setCalConfirmed(prev => ({ ...prev, [msgIndex]: 'done' }));
        setCalPending(prev => { const n = { ...prev }; delete n[msgIndex]; return n; });
      } else {
        setCalConfirmed(prev => ({ ...prev, [msgIndex]: 'error' }));
      }
    } catch {
      setCalConfirmed(prev => ({ ...prev, [msgIndex]: 'error' }));
    }
  };

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || isThinking) return;
    setInput('');
    setError(null);

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    const newThread = [...thread, userMsg];
    setThread(newThread);
    setIsThinking(true);

    try {
      const res = await apiFetch('/api/quinn/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, thread: newThread.slice(-12), timezone }),
      });

      if (!res.ok || !res.body) throw new Error('Quinn unavailable');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, '').trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.type === 'message') {
              // Detect and extract task and calendar action tags
              const taskAction = parseTaskAction(evt.content || '');
              const calAction  = parseCalendarAction(evt.content || '');
              let displayContent = evt.content;
              if (taskAction) displayContent = stripTaskAction(displayContent);
              if (calAction)  displayContent = stripCalendarAction(displayContent);
              const newMsg = {
                role: 'assistant', ai: 'quinn',
                content: displayContent, timestamp: evt.timestamp,
              };
              setThread(prev => {
                const updated = [...prev, newMsg];
                const idx = updated.length - 1;
                // Register pending task action keyed by message index
                if (taskAction) setPending(p => ({ ...p, [idx]: taskAction }));
                // Register pending calendar action keyed by message index
                if (calAction)  setCalPending(p => ({ ...p, [idx]: calAction }));
                // Auto-save every 4 messages so preferences persist without waiting for unmount
                if (updated.length >= 4 && updated.length % 4 === 0) {
                  setTimeout(() => saveSession(updated), 1000);
                }
                // Also save immediately if Quinn is noting a preference
                const lc = (evt.content || '').toLowerCase();
                if (lc.includes('noted') || lc.includes('i\'ll remember') || lc.includes('i\'ve noted') || lc.includes('saved') || lc.includes('got it')) {
                  setTimeout(() => saveSession(updated), 1500);
                }
                // Jarvis observation — fire and forget
                apiFetch('/api/jarvis/observe', {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    room: 'quinn_studio', ai: 'quinn', sessionType: 'quinn',
                    response: evt.content || '',
                    context: {},
                    thread: updated.slice(-6),
                  }),
                }).catch(() => {});
                return updated;
              });
            } else if (evt.type === 'error') {
              setError(evt.message);
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsThinking(false);
    }
  }, [input, thread, isThinking, timezone]);

  const isEmpty = thread.length === 0;

  return (
    <div className="quinn-chat-tab">
      <div className="quinn-thread">
        {isEmpty && (
          <div className="quinn-empty">
            <div className="quinn-empty-crown">👑</div>
            <div className="quinn-empty-heading">{firstName ? `Good to have you, ${firstName}.` : 'Good to have you.'}</div>
            <div className="quinn-empty-sub">I have your calendar, tasks, deals, health reminders, and shopping lists loaded. What do you need?</div>
            <div className="quinn-prompts">
              {QUINN_PROMPTS.map((p, i) => (
                <button key={i} className="quinn-prompt-btn" onClick={() => sendMessage(p)}>{p}</button>
              ))}
            </div>
          </div>
        )}
        {thread.map((msg, i) => (
          <div key={i} className={`quinn-msg quinn-msg--${msg.role}`}>
            {msg.role === 'assistant' && <div className="quinn-msg-avatar">👑</div>}
            <div className="quinn-msg-body">
              {msg.role === 'assistant'
                ? <MarkdownContent text={msg.content} />
                : <p>{msg.content}</p>}
              <div className="quinn-msg-time">
                {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
              {/* Task creation confirm button */}
              {pendingTaskActions[i] && (
                <div className="quinn-task-confirm">
                  {taskConfirmed[i] === 'loading' && <span className="quinn-task-status">Creating...</span>}
                  {taskConfirmed[i] === 'done'    && <span className="quinn-task-status quinn-task-status--done">✅ Task created</span>}
                  {taskConfirmed[i] === 'error'   && <span className="quinn-task-status quinn-task-status--error">⚠ Failed — try again</span>}
                  {!taskConfirmed[i] && (
                    <>
                      <button className="quinn-task-btn quinn-task-btn--confirm"
                        onClick={() => confirmTask(i, pendingTaskActions[i])}>
                        ✓ Create Task
                      </button>
                      <button className="quinn-task-btn quinn-task-btn--cancel"
                        onClick={() => setPending(prev => { const n = { ...prev }; delete n[i]; return n; })}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* Calendar event confirm button */}
              {pendingCalActions[i] && (
                <div className="quinn-task-confirm">
                  {calConfirmed[i] === 'loading' && <span className="quinn-task-status">Adding to calendar...</span>}
                  {calConfirmed[i] === 'done'    && <span className="quinn-task-status quinn-task-status--done">✅ Added to calendar</span>}
                  {calConfirmed[i] === 'error'   && <span className="quinn-task-status quinn-task-status--error">⚠ Failed — try again</span>}
                  {!calConfirmed[i] && (
                    <>
                      <button className="quinn-task-btn quinn-task-btn--confirm"
                        onClick={() => confirmCalendar(i, pendingCalActions[i])}>
                        📅 Add to Calendar
                      </button>
                      <button className="quinn-task-btn quinn-task-btn--cancel"
                        onClick={() => setCalPending(prev => { const n = { ...prev }; delete n[i]; return n; })}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {isThinking && (
          <div className="quinn-msg quinn-msg--assistant">
            <div className="quinn-msg-avatar">👑</div>
            <div className="quinn-msg-body quinn-thinking"><span /><span /><span /></div>
          </div>
        )}
        {error && <div className="quinn-error">⚠ {error}</div>}
        <div ref={bottomRef} />
      </div>

      <div className="quinn-input-bar">
        {thread.length > 0 && (
          <button className="quinn-clear-btn" onClick={() => { saveSession(thread); setThread([]); }}>
            New conversation
          </button>
        )}
        <textarea
          ref={inputRef}
          className="quinn-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Ask Quinn anything..."
          rows={1}
          disabled={isThinking}
        />
        <button className="quinn-send-btn" onClick={() => sendMessage()} disabled={!input.trim() || isThinking}>↑</button>
      </div>
    </div>
  );
}

// ── CALENDAR TAB ─────────────────────────────────────────────────────────────
function CalendarTab() {
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter]     = useState('all'); // all | business | personal
  const [newEvent, setNewEvent] = useState({
    eventName: '', eventType: 'Meeting', date: '', notes: '', isPrivate: false, priority: 'Normal',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const in30 = new Date(); in30.setDate(in30.getDate() + 30);
      const end = in30.toISOString().split('T')[0];
      const res = await apiFetch(`/api/quinn/calendar?start=${today}&end=${end}&includePrivate=true`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch { setEvents([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createEvent = async () => {
    if (!newEvent.eventName || !newEvent.date) return;
    setSaving(true);
    try {
      await apiFetch('/api/quinn/calendar', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEvent),
      });
      setShowForm(false);
      setNewEvent({ eventName: '', eventType: 'Meeting', date: '', notes: '', isPrivate: false, priority: 'Normal' });
      load();
    } catch { /* handle */ }
    setSaving(false);
  };

  const markDone = async (id) => {
    await apiFetch('/api/quinn/calendar', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pageId: id, status: 'Done' }),
    });
    setEvents(prev => prev.filter(e => e.id !== id));
  };

  const filtered = events.filter(e => {
    if (filter === 'business') return !e.isPrivate;
    if (filter === 'personal') return e.isPrivate;
    return true;
  });

  const grouped = {};
  filtered.forEach(e => {
    const key = formatDate(e.date);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(e);
  });

  return (
    <div className="quinn-tab-content">
      <div className="quinn-tab-toolbar">
        <div className="quinn-filter-pills">
          {['all','business','personal'].map(f => (
            <button key={f} className={`quinn-filter-pill ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'business' ? '💼 Business' : '🏠 Personal'}
            </button>
          ))}
        </div>
        <button className="quinn-add-btn" onClick={() => setShowForm(!showForm)}>+ Add Event</button>
      </div>

      {showForm && (
        <div className="quinn-form-card">
          <div className="quinn-form-row">
            <input className="quinn-form-input" placeholder="Event name *" value={newEvent.eventName}
              onChange={e => setNewEvent(p => ({ ...p, eventName: e.target.value }))} />
            <input className="quinn-form-input" type="datetime-local" value={newEvent.date}
              onChange={e => setNewEvent(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div className="quinn-form-row">
            <select className="quinn-form-select" value={newEvent.eventType}
              onChange={e => setNewEvent(p => ({ ...p, eventType: e.target.value }))}>
              {Object.keys(EVENT_COLORS).map(t => <option key={t}>{t}</option>)}
            </select>
            <select className="quinn-form-select" value={newEvent.priority}
              onChange={e => setNewEvent(p => ({ ...p, priority: e.target.value }))}>
              <option>High</option><option>Normal</option><option>Low</option>
            </select>
            <label className="quinn-form-check">
              <input type="checkbox" checked={newEvent.isPrivate}
                onChange={e => setNewEvent(p => ({ ...p, isPrivate: e.target.checked }))} />
              <span>Private</span>
            </label>
          </div>
          <textarea className="quinn-form-textarea" placeholder="Notes (optional)" value={newEvent.notes}
            onChange={e => setNewEvent(p => ({ ...p, notes: e.target.value }))} rows={2} />
          <div className="quinn-form-actions">
            <button className="quinn-form-cancel" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="quinn-form-save" onClick={createEvent} disabled={saving || !newEvent.eventName || !newEvent.date}>
              {saving ? 'Saving...' : 'Add Event'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="quinn-loading">Loading calendar...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="quinn-empty-state">No upcoming events in the next 30 days.</div>
      ) : (
        <div className="quinn-calendar-list">
          {Object.entries(grouped).map(([day, dayEvents]) => (
            <div key={day} className="quinn-day-group">
              <div className="quinn-day-label">{day}</div>
              {dayEvents.map(event => (
                <div key={event.id} className="quinn-event-card" style={{ borderLeftColor: eventColor(event.type) }}>
                  <div className="quinn-event-top">
                    <div className="quinn-event-type-dot" style={{ background: eventColor(event.type) }} />
                    <div className="quinn-event-info">
                      <div className="quinn-event-name">
                        {event.isPrivate && <span className="quinn-private-badge">🔒</span>}
                        {event.eventName}
                        {event.priority === 'High' && <span className="quinn-priority-badge">⚠️</span>}
                      </div>
                      <div className="quinn-event-meta">
                        <span className="quinn-event-type-label">{event.type}</span>
                        {event.accountName && <span className="quinn-event-account">· {event.accountName}</span>}
                        {event.date && event.date.includes('T') && (
                          <span className="quinn-event-time">
                            · {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      {event.notes && <div className="quinn-event-notes">{event.notes}</div>}
                    </div>
                    <button className="quinn-event-done-btn" onClick={() => markDone(event.id)} title="Mark done">✓</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── RESEARCH TAB ─────────────────────────────────────────────────────────────
function ResearchTab() {
  // P0.30 30.2 — the renameable persona name, from context.
  const { workflowName } = useProfile();
  const [showForm, setShowForm] = useState(false);
  const [researching, setResearching] = useState(false);
  const [result, setResult]     = useState(null);
  const [form, setForm] = useState({
    request: '', type: 'Travel', travelDates: '', destination: '', budget: '',
  });

  const runResearch = async () => {
    if (!form.request.trim()) return;
    setResearching(true);
    setResult(null);
    try {
      const res = await apiFetch('/api/quinn/research', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ error: err.message });
    }
    setResearching(false);
  };

  return (
    <div className="quinn-tab-content">
      <div className="quinn-tab-toolbar">
        <div className="quinn-tab-desc">{workflowName} researches travel, shopping, gifts, and vendors using your stored preferences, then presents options — you decide and book.</div>
        <button className="quinn-add-btn" onClick={() => { setShowForm(!showForm); setResult(null); }}>
          {showForm ? 'Cancel' : '+ New Research'}
        </button>
      </div>

      {showForm && (
        <div className="quinn-form-card">
          <div className="quinn-form-row">
            <input className="quinn-form-input quinn-form-input--wide" placeholder="What do you need researched? *"
              value={form.request} onChange={e => setForm(p => ({ ...p, request: e.target.value }))} />
            <select className="quinn-form-select" value={form.type}
              onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              <option>Travel</option>
              <option>Shopping</option>
              <option>Personal Purchase</option>
              <option>Gift</option>
              <option>Vendor Research</option>
              <option>Event Planning</option>
              <option>Other</option>
            </select>
          </div>
          {form.type === 'Travel' && (
            <div className="quinn-form-row">
              <input className="quinn-form-input" placeholder="Destination (e.g. Chicago, IL)"
                value={form.destination} onChange={e => setForm(p => ({ ...p, destination: e.target.value }))} />
              <input className="quinn-form-input" placeholder="Travel dates (e.g. March 28-30)"
                value={form.travelDates} onChange={e => setForm(p => ({ ...p, travelDates: e.target.value }))} />
              <input className="quinn-form-input" placeholder="Budget (optional)"
                value={form.budget} onChange={e => setForm(p => ({ ...p, budget: e.target.value }))} />
            </div>
          )}
          <div className="quinn-form-actions">
            <button className="quinn-form-save" onClick={runResearch}
              disabled={researching || !form.request.trim()}>
              {researching ? 'Researching...' : 'Run Research'}
            </button>
          </div>
        </div>
      )}

      {researching && (
        <div className="quinn-research-loading">
          <div className="quinn-thinking-inline"><span /><span /><span /></div>
          <span>Quinn is searching with your preferences...</span>
        </div>
      )}

      {result && !result.error && (
        <div className="quinn-research-result">
          {result.fromCache && (
            <div className="quinn-research-cached">📋 Found existing research on this topic.</div>
          )}
          <div className="quinn-research-summary">
            <MarkdownContent text={result.summary} />
          </div>
          {result.bookingLinks?.length > 0 && (
            <div className="quinn-research-links">
              <div className="quinn-research-links-label">Quick links</div>
              {result.bookingLinks.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noreferrer" className="quinn-research-link">
                  {l.title}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {result?.error && (
        <div className="quinn-error">Research failed: {result.error}</div>
      )}

      {!showForm && !researching && !result && (
        <div className="quinn-empty-state">
          <div className="quinn-empty-icon">🔍</div>
          <div>Start a research thread — travel, shopping, gifts, vendors.</div>
          <div className="quinn-empty-hint">Quinn uses your stored preferences to personalize every search.</div>
        </div>
      )}
    </div>
  );
}

// ── PREFERENCES TAB ───────────────────────────────────────────────────────────
const PREF_CATEGORIES = [
  'Travel', 'Family', 'Shopping', 'Health', 'Professional',
  'Food & Dining', 'Sports & Leisure', 'Finance', 'Subscriptions', 'Personal',
];

function PreferencesTab() {
  // P0.30 30.2 — `workflow` is renameable in settings but had no resolver
  // until this phase, so this screen said "Quinn" whatever the tenant chose.
  const { workflowName } = useProfile();
  const [prefs, setPrefs]       = useState({});
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Travel');
  const [saving, setSaving]     = useState(false);
  const [newPref, setNewPref]   = useState({ preference: '', category: 'Travel', detail: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/quinn/preferences');
      const data = await res.json();
      setPrefs(data.grouped || {});
    } catch { setPrefs({}); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePref = async () => {
    if (!newPref.preference.trim()) return;
    setSaving(true);
    try {
      await apiFetch('/api/quinn/preferences', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newPref, learnedFrom: 'Manual Entry' }),
      });
      setNewPref({ preference: '', category: activeCategory, detail: '' });
      setShowForm(false);
      load();
    } catch { /* handle */ }
    setSaving(false);
  };

  const allCategories = [...new Set([...PREF_CATEGORIES, ...Object.keys(prefs)])];
  const categoryPrefs = prefs[activeCategory] || [];

  const CATEGORY_HINTS = {
    'Travel':         'Airlines, loyalty programs, seat preferences, home airport, TSA PreCheck, Global Entry',
    'Family':         'Family members with names/birthdays/ages, important anniversaries, recurring events',
    'Shopping':       'Preferred stores, brands, size/fit preferences, shopping habits',
    'Health':         'Health providers, preferred pharmacy, appointment cadences, medical preferences',
    'Professional':   'Work style, meeting preferences, tools, professional memberships',
    'Food & Dining':  'Dietary restrictions, favorite cuisines, restaurants for client dinners',
    'Sports & Leisure':'Sports teams followed, hobbies, entertainment preferences',
    'Finance':        'Budget ranges for travel/entertainment, expense categories, accountant info',
    'Subscriptions':  'Recurring services, renewal dates, account details',
    'Personal':       'Anything else Quinn should know to serve you better',
  };

  return (
    <div className="quinn-tab-content quinn-prefs-layout">
      {/* Category sidebar */}
      <div className="quinn-prefs-sidebar">
        {allCategories.map(cat => (
          <button key={cat}
            className={`quinn-prefs-cat ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => { setActiveCategory(cat); setShowForm(false); }}>
            <span>{cat}</span>
            {prefs[cat]?.length > 0 && <span className="quinn-prefs-count">{prefs[cat].length}</span>}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="quinn-prefs-content">
        <div className="quinn-prefs-header">
          <div>
            <div className="quinn-prefs-cat-title">{activeCategory}</div>
            {CATEGORY_HINTS[activeCategory] && (
              <div className="quinn-prefs-hint">{CATEGORY_HINTS[activeCategory]}</div>
            )}
          </div>
          <button className="quinn-add-btn" onClick={() => { setShowForm(!showForm); setNewPref(p => ({ ...p, category: activeCategory })); }}>
            + Add
          </button>
        </div>

        {showForm && (
          <div className="quinn-form-card">
            <input className="quinn-form-input quinn-form-input--wide"
              placeholder="Preference name (e.g. 'Preferred airline')"
              value={newPref.preference}
              onChange={e => setNewPref(p => ({ ...p, preference: e.target.value }))} />
            <textarea className="quinn-form-textarea"
              placeholder="Detail (e.g. 'United Airlines, MileagePlus #XXXXXX, Gold status, always window seat')"
              value={newPref.detail}
              onChange={e => setNewPref(p => ({ ...p, detail: e.target.value }))}
              rows={3} />
            <div className="quinn-form-actions">
              <button className="quinn-form-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="quinn-form-save" onClick={savePref} disabled={saving || !newPref.preference.trim()}>
                {saving ? 'Saving...' : 'Save Preference'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="quinn-loading">Loading preferences...</div>
        ) : categoryPrefs.length === 0 ? (
          <div className="quinn-empty-state">
            <div className="quinn-empty-icon">🧠</div>
            <div>No {activeCategory} preferences stored yet.</div>
            <div className="quinn-empty-hint">Add them above, or just tell Quinn in chat — {workflowName} will save them automatically.</div>
          </div>
        ) : (
          <div className="quinn-prefs-list">
            {categoryPrefs.map((pref, i) => (
              <div key={i} className="quinn-pref-item">
                <div className="quinn-pref-name">{pref.preference}</div>
                <div className="quinn-pref-detail">{pref.detail}</div>
                {pref.learnedFrom && (
                  <div className="quinn-pref-source">
                    {pref.learnedFrom === 'Conversation' ? '💬 Learned from conversation' :
                     pref.learnedFrom === 'Manual Entry' ? '✏️ Manually entered' : pref.learnedFrom}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN QUINN PAGE ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'chat',        label: '💬 Chat',        desc: 'Talk to Quinn' },
  { id: 'calendar',   label: '📅 Calendar',     desc: '30-day view' },
  { id: 'research',   label: '🔍 Research',     desc: 'Travel & shopping' },
  { id: 'preferences',label: '🧠 Preferences',  desc: 'Quinn\'s knowledge' },
];

export default function QuinnPage({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('chat');

  return (
    <div className="quinn-page">
      {/* Header */}
      <div className="quinn-header">
        <div className="quinn-header-left">
          <span className="dm-page-mark dm-page-mark--quinn">👑</span>
          <div>
            <div className="quinn-title">Quinn</div>
            <div className="quinn-subtitle">Executive & Life Assistant</div>
          </div>
        </div>
      </div>
      {/* Tab nav — OUTSIDE the header, as Research/Studio/Victor do. Inside it,
          the tabs competed with the title for the same row and the page read
          as a different shape from the other three. */}
      <div className="quinn-tab-nav">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`quinn-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

      {/* Tab content */}
      <div className="quinn-tab-body">
        {activeTab === 'chat'        && <ChatTab onNavigate={onNavigate} />}
        {activeTab === 'calendar'    && <CalendarTab />}
        {activeTab === 'research'    && <ResearchTab />}
        {activeTab === 'preferences' && <PreferencesTab />}
      </div>
    </div>
  );
}
